import { Alert, Box, Button, Group, Loader, Paper, Stack, Switch, Text, Title, Tooltip } from '@mantine/core'
import { useListState } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconPlus } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import type { GetSponsorTopicsResponse, TopicResponse } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

import { ConfirmChangesModal } from './ConfirmChangesModal'
import { TopicEntry } from './TopicEntry'
import {
  calculatePriorities,
  calculateTopicModuleCounts,
  calculateTopicWarnings,
  compareTopicArrays,
  findDuplicateNumber,
  pollUntilDataMatches,
  sortTopicsByPriority,
  validateTopics,
} from './utils'

interface TopicsPanelProps {
  showTutorialText: boolean
  showModuleCounts: boolean
  registerUnsavedChangesChecker: (tabKey: 'topics', checker: () => boolean, onDiscard: () => void) => void
}

// Toggle this flag to bypass the confirmation dialog
const BYPASS_CONFIRMATION_DIALOG = true

export function TopicsPanel({
  showTutorialText = false,
  showModuleCounts = false,
  registerUnsavedChangesChecker,
}: TopicsPanelProps) {
  const { api, uiStore } = useGlobalState()
  const [isPending, setIsPending] = useState(false)
  const isMobile = useCheckMobileScreen()
  const { topicsEnabled, hasDefinedTopics, refetch: refetchTopicsEnabled } = useTopicsEnabled()
  const [showDisableWarning, setShowDisableWarning] = useState(false)

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: () => api.getSponsorTopics(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const contentModulesQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
  })

  const [originalTopics, setOriginalTopics] = useState<GetSponsorTopicsResponse>([])
  const [topics, topicHandlers] = useListState<TopicResponse>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [topicsWithWarnings, setTopicsWithWarnings] = useState<Set<string>>(new Set())
  const [newTopicId, setNewTopicId] = useState<string | null>(null)
  const [pendingDeleteTopicId, setPendingDeleteTopicId] = useState<string | null>(null)

  // Mutation to toggle topics enabled
  const toggleTopicsEnabledMutation = useMutation({
    mutationFn: (enabled: boolean) => api.updateTopicsEnabled(enabled),
    onMutate: () => uiStore.showLoadingOverlay(),
    onSuccess: async () => {
      api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] })
      await refetchTopicsEnabled()
      uiStore.hideLoadingOverlay()
      notifications.show({
        title: 'Success',
        message: `Topics have been ${topicsEnabled ? 'disabled' : 'enabled'}`,
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => {
      uiStore.hideLoadingOverlay()
      notifications.show({
        title: 'Error',
        message: 'Failed to update topics setting',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const handleToggleTopics = () => {
    if (topicsEnabled && hasDefinedTopics) {
      // Show warning before disabling
      setShowDisableWarning(true)
    } else {
      toggleTopicsEnabledMutation.mutate(!topicsEnabled)
    }
  }

  const handleConfirmDisableTopics = () => {
    toggleTopicsEnabledMutation.mutate(false)
    setShowDisableWarning(false)
  }

  // Validation Logic
  // ------------------------------------------------------------

  const validationErrors = useMemo(() => {
    return validateTopics(topics)
  }, [topics])

  const hasValidationErrors = useMemo(() => {
    return validationErrors.length > 0
  }, [validationErrors])

  // Update State from API
  // ------------------------------------------------------------
  useEffect(() => {
    if (topicsQuery.data) {
      const sortedTopics = sortTopicsByPriority(topicsQuery.data)
      topicHandlers.setState(sortedTopics)
      setOriginalTopics(sortedTopics)
    }
  }, [topicsQuery.data])
  // Count modules per topic using direct topic ID mapping
  const topicModuleCounts = useMemo(() => {
    return calculateTopicModuleCounts(topics, contentModulesQuery.data || [])
  }, [topics, contentModulesQuery.data])

  // Update the warning effect to use ID-based module counts
  useEffect(() => {
    const warnings = calculateTopicWarnings(topics, originalTopics, topicModuleCounts)
    setTopicsWithWarnings(warnings)
  }, [topics, topicModuleCounts, originalTopics])

  // Topic Functionality
  // ------------------------------------------------------------
  const handleAddTopic = () => {
    const newTopicId = nanoid()

    const newTopic: TopicResponse = {
      id: newTopicId,
      key: newTopicId,
      displayName: `Topic ${topics.length + 1}`,
      subtopics: [],
      priority: 0,
      skId: null,
      notRemovable: false,
    }
    topicHandlers.append(newTopic)

    // Set the new topic ID to trigger auto-focus
    setNewTopicId(newTopicId)
  }

  const handleDuplicateTopic = (topicId: string) => {
    const topicToDuplicate = topics.find(t => t.id === topicId)

    if (!topicToDuplicate) return

    const newTopicId = nanoid()
    const nextNumber = findDuplicateNumber(topics, topicToDuplicate.displayName)

    const newTopic: TopicResponse = {
      ...topicToDuplicate,
      id: newTopicId,
      key: newTopicId,
      displayName: `${topicToDuplicate.displayName} (${nextNumber})`,
      subtopics: [],
      priority: 0,
      skId: null,
      notRemovable: false,
    }

    topicHandlers.append(newTopic)
    // Set the new topic ID to trigger auto-focus
    setNewTopicId(newTopicId)
  }

  const handleDeleteTopic = (topicId: string) => {
    if (topicModuleCounts[topicId] > 0) {
      setPendingDeleteTopicId(topicId)
      return
    }

    topicHandlers.setState(prevTopics => prevTopics.filter(topic => topic.id !== topicId))
  }

  const handleConfirmDeleteTopic = () => {
    if (pendingDeleteTopicId) {
      topicHandlers.setState(prevTopics => prevTopics.filter(topic => topic.id !== pendingDeleteTopicId))
      setPendingDeleteTopicId(null)
    }
  }

  const handleTopicNameChange = (topicId: string, newName: string) => {
    topicHandlers.setState(prevTopics =>
      prevTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              displayName: newName,
            }
          : topic,
      ),
    )
  }

  // Change Detection
  // ------------------------------------------------------------
  const hasTopicChanges = useMemo(() => {
    return !compareTopicArrays(originalTopics, topics)
  }, [originalTopics, topics])

  const hasChanges = useMemo(() => {
    return hasTopicChanges
  }, [hasTopicChanges])

  // ------------------------------------------------------------
  const handleCancel = useCallback(() => {
    topicHandlers.setState(originalTopics)
  }, [originalTopics, topicHandlers])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('topics', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  // Helper Functions
  // ------------------------------------------------------------
  const pollUntilTopicsSync = async (expectedTopics: TopicResponse[]) => {
    // Poll until server data matches our expected state
    await pollUntilDataMatches(
      async () => {
        await api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'topics'] })
        const refetchResult = await topicsQuery.refetch()
        return refetchResult.data || []
      },
      serverData => compareTopicArrays(expectedTopics, serverData),
    )
  }

  const updateTopicsMutation = useMutation({
    mutationFn: async (params: { topics: TopicResponse[] }): Promise<void> => {
      try {
        await api.updateTopics(params)
      } catch (error) {
        console.error('Error updating topics:', error)
        if (topicsQuery.data) {
          topicHandlers.setState(topicsQuery.data)
        }
        throw error
      }
    },
    onSuccess: async (_, params) => {
      await pollUntilTopicsSync(params.topics)
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
    },
    onError: error => {
      console.error('Mutation error:', error)
    },
  })

  const handleConfirmApply = async () => {
    setIsPending(true)
    try {
      const updatedTopics = calculatePriorities(topics)

      await updateTopicsMutation.mutateAsync({
        topics: updatedTopics,
      })

      setOriginalTopics([...topics])
    } catch (error) {
      console.error('Error applying changes:', error)
      // Revert to original state on error
      topicHandlers.setState(originalTopics)
    } finally {
      setIsPending(false)
      setShowConfirmModal(false)
      setNewTopicId(null)
    }
  }

  // If topics are disabled, show the enable toggle
  if (!topicsEnabled) {
    return (
      <Stack>
        <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
          <Group justify="space-between" align="center" mb="md">
            {isMobile ? (
              <Text fw={600} size="xl">
                Topics
              </Text>
            ) : (
              <Title order={4}>Topics</Title>
            )}
          </Group>

          <Stack gap="md">
            <Text size="md" c="dimmed">
              Topics allow you to categorize your content modules. When enabled, you can create topics and assign
              content modules to them.
            </Text>

            <Group>
              <Switch
                checked={topicsEnabled}
                onChange={handleToggleTopics}
                label="Enable Topics"
                size="md"
                disabled={toggleTopicsEnabledMutation.isPending}
              />
            </Group>
          </Stack>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" align="center" mb="md">
          {isMobile ? (
            <Text fw={600} size="xl" mb="md">
              Define Topics for Content Modules
            </Text>
          ) : (
            <Title order={4}>Define Topics for Content Modules</Title>
          )}
          <Group>
            <Switch
              checked={topicsEnabled}
              onChange={handleToggleTopics}
              label="Enabled"
              size="sm"
              disabled={toggleTopicsEnabledMutation.isPending}
            />
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Tooltip label="Save becomes available when you make changes. Save or Cancel any changes.">
              <Button
                onClick={() => (BYPASS_CONFIRMATION_DIALOG ? handleConfirmApply() : setShowConfirmModal(true))}
                disabled={!hasChanges || hasValidationErrors}
                loading={BYPASS_CONFIRMATION_DIALOG ? isPending : false}>
                Save
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {showTutorialText && (
          <Text size="md" c="dimmed" mb="md">
            Create Topics to help organize your Content Modules and Experts. Add Topics by enabling Topics and then
            clicking &ldquo;Add Topic.&rdquo; Disabling Topics will hide topic-related features.
          </Text>
        )}

        {validationErrors.length > 0 && (
          <Alert
            variant="light"
            color="red"
            radius="md"
            title="Validation Errors"
            icon={<IconAlertCircle size={16} />}
            py="xs"
            mb="md">
            <Stack gap="xs">
              {validationErrors.map((error, index) => (
                <Text key={index} size="sm">
                  {error}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" align="center" mb="md">
          <Group>
            <Button leftSection={<IconPlus size={14} />} onClick={handleAddTopic}>
              Add Topic
            </Button>
          </Group>
        </Group>

        <Group justify="space-between" align="top">
          {topicsQuery.isLoading && !topicsQuery.data ? (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          ) : topicsQuery.isError ? (
            <Text c="red">Error loading topics</Text>
          ) : !topics.length ? (
            <Box
              py="xl"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Text c="dimmed" ta="center" size="xl">
                No topics found
              </Text>
            </Box>
          ) : (
            <Stack gap="sm" style={{ width: '100%' }}>
              {topics.map(item => (
                <TopicEntry
                  key={item.id}
                  item={item}
                  menuProps={{
                    onDelete: () => handleDeleteTopic(item.id ?? ''),
                    onDuplicate: () => handleDuplicateTopic(item.id ?? ''),
                    canDelete: true,
                    canDuplicate: true,
                  }}
                  moduleCount={topicModuleCounts[item.id ?? ''] ?? 0}
                  onDisplayNameChange={handleTopicNameChange}
                  showWarning={topicsWithWarnings.has(item.id ?? '')}
                  shouldFocus={item.id === newTopicId}
                  showModuleCounts={showModuleCounts}
                />
              ))}
            </Stack>
          )}
        </Group>
      </Paper>

      <ConfirmChangesModal
        title="Are you sure?"
        description="Making changes to the Topics can impact the experience of Members who have already set up their topics."
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        isLoading={isPending}
      />

      <ConfirmChangesModal
        title="Disable Topics?"
        description="Disabling topics will hide topic-related features across the application. Your existing topics and their assignments will be removed."
        opened={showDisableWarning}
        onClose={() => setShowDisableWarning(false)}
        onConfirm={handleConfirmDisableTopics}
        isLoading={toggleTopicsEnabledMutation.isPending}
      />

      <ConfirmChangesModal
        title="Delete topic?"
        description="Deleting this topic will remove it from the content modules it is associated with. Those content modules will then have no topic."
        opened={pendingDeleteTopicId !== null}
        onClose={() => setPendingDeleteTopicId(null)}
        onConfirm={handleConfirmDeleteTopic}
        isLoading={false}
        cancelLabel="Keep topic"
        confirmLabel="Delete topic"
        confirmColor="red"
      />
    </Stack>
  )
}
